<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Icon from '../components/Icon.svelte';
  import { ApiError } from '../lib/api';
  import { acceptPasswordReset } from '../lib/admin-api';

  let password = $state('');
  let confirm = $state('');
  let submitting = $state(false);
  let errorMsg = $state<string | null>(null);
  let done = $state(false);

  /** Parse `?token=…` з хеш-роута. */
  function readToken(): string {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return '';
    return new URLSearchParams(hash.slice(qIdx + 1)).get('token') ?? '';
  }
  const token = readToken();

  const passwordValid = $derived(password.length >= 12);
  const passwordsMatch = $derived(password.length > 0 && password === confirm);
  const strength = $derived.by(() => {
    let s = 0;
    if (password.length >= 12) s++;
    if (/[A-Z]/.test(password)) s++;
    if (/[0-9]/.test(password)) s++;
    if (/[^A-Za-z0-9]/.test(password)) s++;
    return s;
  });

  async function onSubmit(e: SubmitEvent) {
    e.preventDefault();
    if (!passwordValid || !passwordsMatch) return;
    submitting = true;
    errorMsg = null;
    try {
      await acceptPasswordReset({ token, password });
      done = true;
      setTimeout(() => push('/login'), 1500);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'password-reset.not_found') errorMsg = 'Лінк не знайдено';
        else if (err.code === 'password-reset.expired') errorMsg = 'Лінк прострочено. Попросіть адміністратора створити новий.';
        else if (err.code === 'password-reset.already_used') errorMsg = 'Цей лінк вже було використано';
        else if (err.code === 'password-reset.user_disabled') errorMsg = 'Користувач заблокований. Зверніться до адміністратора.';
        else errorMsg = err.message;
      } else {
        errorMsg = String(err);
      }
    } finally {
      submitting = false;
    }
  }
</script>

<div class="auth-bg min-h-screen flex items-center justify-center p-6">
  <form
    onsubmit={onSubmit}
    class="auth-card w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-9 backdrop-blur-xl shadow-2xl"
  >
    <div class="flex items-center gap-3 mb-7">
      <div class="auth-logo-mark">F</div>
      <div>
        <div class="text-sm font-bold text-white tracking-wide">FocusOnYou</div>
        <div class="text-[10px] uppercase tracking-[2px] text-white/50 mt-0.5">Marketing Analytics</div>
      </div>
    </div>

    <h1 class="text-xl font-semibold text-white mb-1.5">Скидання пароля</h1>
    <p class="text-xs text-white/60 mb-6 leading-relaxed">
      Введіть новий пароль для свого акаунту. Після збереження ви зможете увійти.
    </p>

    {#if !token}
      <div class="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
        Токен скидання відсутній у посиланні. Попросіть адміністратора створити новий.
      </div>
    {:else if done}
      <div class="flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-200">
        <Icon name="statusDone" size={15} class="shrink-0" /> Пароль оновлено. Перенаправляємо на сторінку входу…
      </div>
    {:else}
      <label for="reset-password" class="block text-xs text-white/65 font-medium mb-1.5">Новий пароль</label>
      <input
        id="reset-password"
        type="password"
        bind:value={password}
        required
        minlength="12"
        autocomplete="new-password"
        placeholder="мінімум 12 символів"
        class="auth-field block w-full rounded-lg bg-white/[0.07] border border-white/20 px-3.5 py-2.5 text-sm text-white mb-2 focus:border-[var(--color-pu-2)] focus:outline-none"
      />
      <div class="flex gap-1 mb-2">
        {#each [1, 2, 3, 4] as i (i)}
          <div
            class="flex-1 h-1 rounded-full"
            style={strength >= i
              ? 'background: linear-gradient(90deg, var(--color-pu), var(--color-pu-2));'
              : 'background: rgba(255,255,255,0.12);'}
          ></div>
        {/each}
      </div>
      <div class="text-[10.5px] text-white/50 mb-3.5">
        Складність:
        <strong class="text-emerald-300">
          {['Слабкий', 'Слабкий', 'Середній', 'Сильний', 'Дуже сильний'][strength]}
        </strong>
        · {strength}/4
      </div>

      <label for="reset-confirm" class="block text-xs text-white/65 font-medium mb-1.5">Підтвердження пароля</label>
      <input
        id="reset-confirm"
        type="password"
        bind:value={confirm}
        required
        autocomplete="new-password"
        class="auth-field block w-full rounded-lg bg-white/[0.07] border border-white/20 px-3.5 py-2.5 text-sm text-white mb-4 focus:border-[var(--color-pu-2)] focus:outline-none"
      />
      {#if confirm.length > 0 && !passwordsMatch}
        <div class="mb-3 text-xs text-amber-300">Паролі не співпадають</div>
      {/if}

      {#if errorMsg}
        <div class="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
          {errorMsg}
        </div>
      {/if}

      <button
        type="submit"
        disabled={submitting || !passwordValid || !passwordsMatch}
        class="w-full rounded-lg py-3 text-sm font-semibold text-white shadow-lg disabled:opacity-50"
        style="background: linear-gradient(135deg, var(--color-pu), var(--color-pu-2));"
      >
        {submitting ? 'Зберігаємо…' : 'Встановити новий пароль →'}
      </button>
    {/if}
  </form>
</div>

<style>
  .auth-bg {
    background: linear-gradient(165deg, var(--color-dark) 0%, var(--color-dark-3) 60%, #6b3a9e 100%);
  }
  .auth-logo-mark {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--color-pu-2), var(--color-pu-mid));
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 800;
    font-size: 16px;
  }
</style>
